import React, { useEffect, useState } from 'react';
import { PlusIcon } from '../../icons';
import { courtAPI } from '../../services/api';
import { normalizeCourtCase } from '../../utils/courtCases';

const CaseReferenceField = ({
  id = 'case-reference',
  label = 'Case / docket reference',
  value,
  onChange,
  required = true,
  disabled = false,
  allowCreate = true,
  onCaseCreated,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [createMessage, setCreateMessage] = useState('');

  const loadSuggestions = async () => {
    try {
      const response = await courtAPI.getCourtCases({ per_page: 50 });
      const items = response.data?.data || response.data || [];
      if (Array.isArray(items)) {
        setSuggestions(
          items.map((item) => {
            const normalized = normalizeCourtCase(item);
            return {
              reference: normalized.docket_number,
              title: normalized.title,
            };
          })
        );
      }
    } catch {
      setSuggestions([]);
    }
  };

  useEffect(() => {
    loadSuggestions();
  }, []);

  const listId = `${id}-suggestions`;

  const handleCreateCase = async () => {
    const docket = value.trim();
    if (!docket || !newTitle.trim()) {
      setCreateMessage('Enter a docket number above and a case title.');
      return;
    }

    setCreating(true);
    setCreateMessage('');

    try {
      const response = await courtAPI.createCourtCase({
        docket_number: docket,
        title: newTitle.trim(),
        status: 'active',
      });
      const created = normalizeCourtCase(response.data?.data || response.data || {
        docket_number: docket,
        title: newTitle.trim(),
      });
      setSuggestions((prev) => [
        { reference: created.docket_number, title: created.title },
        ...prev.filter((s) => s.reference !== created.docket_number),
      ]);
      setCreateMessage('Case registered successfully.');
      setShowCreate(false);
      setNewTitle('');
      onCaseCreated?.(created);
    } catch {
      setSuggestions((prev) => [
        { reference: docket, title: newTitle.trim() },
        ...prev.filter((s) => s.reference !== docket),
      ]);
      setCreateMessage('Saved locally — court API unavailable.');
      setShowCreate(false);
      setNewTitle('');
      onCaseCreated?.({ docket_number: docket, title: newTitle.trim() });
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-legal-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        id={id}
        type="text"
        list={listId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder="e.g. CR/0124/2026 or SUPL/045/2025"
        className="w-full px-3 py-2 border border-legal-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-court-500 focus:border-court-500 text-sm disabled:bg-legal-50"
      />
      <datalist id={listId}>
        {suggestions.map((item) => (
          <option key={`${item.reference}-${item.title}`} value={item.reference}>
            {item.title ? `${item.reference} — ${item.title}` : item.reference}
          </option>
        ))}
      </datalist>
      <p className="mt-1 text-xs text-legal-500">
        Select an existing case or enter a docket number manually.
      </p>

      {allowCreate && !disabled && (
        <div className="mt-3">
          {!showCreate ? (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-1 text-xs text-court-600 hover:text-court-800 font-medium"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Register new case
            </button>
          ) : (
            <div className="rounded-lg border border-legal-200 bg-legal-50 p-3 space-y-2">
              <p className="text-xs font-medium text-legal-700">
                Register docket <span className="font-semibold">{value || '…'}</span> as a new case
              </p>
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Case title (e.g. Republic v. Mensah)"
                className="w-full px-3 py-2 border border-legal-300 rounded-lg text-sm bg-white"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleCreateCase}
                  disabled={creating}
                  className="px-3 py-1.5 bg-court-600 text-white text-xs rounded-lg hover:bg-court-700 disabled:opacity-50"
                >
                  {creating ? 'Saving…' : 'Save case'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreate(false);
                    setCreateMessage('');
                  }}
                  className="px-3 py-1.5 text-xs text-legal-600"
                >
                  Cancel
                </button>
              </div>
              {createMessage && (
                <p className="text-xs text-legal-600">{createMessage}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CaseReferenceField;
